import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { composingRoute, prewarmAgentSession } from "@/lib/agent-prewarm";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import { useInputBarTarget } from "@/stores/input-bar-target-store";

/** A typing or speaking pause this long sends the draft for the topic check. */
export const DRAFT_PAUSE_MS = 600;

/** Drafts sent and not yet answered, across this window's inputs, with the
 *  conversation each was checked against (none when it follows the selection). */
const pendingDrafts = new Map<string, string | undefined>();

function newDraftId(): string {
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Send one composing draft: the backend embeds it for the send and predicts
 *  whether sending it now opens a new conversation for its topic. Returns the
 *  dedupe key and draft id, or null when the destination cannot split. */
function sendPromptDraft(
  text: string,
  lastKey: string | null,
): { key: string; draftId: string; conversationId?: string } | null {
  const trimmed = text.trim();
  const ws = getCurrentDenkerWs();
  const route = ws && trimmed ? composingRoute() : null;
  // A new chat cannot split, and a sub-agent's input stays with its parent.
  if (
    !ws ||
    !route ||
    route.startNewConversation ||
    isSubagentTarget(useInputBarTarget.getState().target)
  ) {
    return null;
  }
  const target = route.target;
  const conversationId = target?.conversationId;
  const key = `${conversationId ?? "current"}:${trimmed}`;
  if (key === lastKey) return null;
  const draftId = newDraftId();
  // The viewed project is only a hint; an explicit target names its own chat.
  ws.sendRaw("prompt.draft", {
    ...(route.viewedProjectId ? { project_id: route.viewedProjectId } : {}),
    text: trimmed,
    draft_id: draftId,
    ...(target
      ? {
          conversation_id: target.conversationId,
          project_agent_id: target.projectAgentId,
        }
      : {}),
  });
  return { key, draftId, conversationId };
}

/** The backend's answer for a pending draft: a likely split warms a fresh
 *  process for the new conversation, on the runtime of the chat it leaves, so
 *  the send has no cold start. A stale answer, or one for another window's
 *  draft, is ignored. */
export function applyPromptDraftTopic(data: {
  split_likely?: unknown;
  draft_id?: unknown;
}): void {
  if (typeof data.draft_id !== "string" || !pendingDrafts.has(data.draft_id)) {
    return;
  }
  const splitFromConversationId = pendingDrafts.get(data.draft_id);
  pendingDrafts.delete(data.draft_id);
  if (data.split_likely === true) {
    prewarmAgentSession("draft_new_topic", {
      startNewConversation: true,
      splitFromConversationId,
    });
  }
}

/** Debounces one input's drafts and sends identical text once. A newer draft
 *  supersedes the input's unanswered one; an empty draft (sent or cleared)
 *  cancels the pending send and drops the answer still on its way. */
export function createDraftScheduler(): {
  schedule: (text: string) => void;
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastKey: string | null = null;
  let pendingId: string | null = null;
  const forgetPending = () => {
    if (pendingId) pendingDrafts.delete(pendingId);
    pendingId = null;
  };
  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return {
    cancel,
    schedule: (text) => {
      cancel();
      if (!text.trim()) {
        forgetPending();
        lastKey = null;
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        const sent = sendPromptDraft(text, lastKey);
        if (!sent) return;
        forgetPending();
        lastKey = sent.key;
        pendingId = sent.draftId;
        pendingDrafts.set(sent.draftId, sent.conversationId);
      }, DRAFT_PAUSE_MS);
    },
  };
}
